import React, {useState, useEffect, useCallback, useContext} from 'react';
import PropTypes from 'prop-types';

import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import {ConsentGroup} from '~/src/utilities/constants/consent';
import {SettingsContext} from '~/src/components/context/SettingsContext';

const ConsentContext = React.createContext({
    groups: [],
    allowsPerformance: !isEnabled(Feature.CMP),
    allowsFunctional: !isEnabled(Feature.CMP),
    allowsTargeting: !isEnabled(Feature.CMP),
    allowsAll: !isEnabled(Feature.CMP)
});

const activeConsentGroups = () => {
    if (process.browser) {
        return (window.OnetrustActiveGroups || '').split(',').filter((n) => n);
    }
    return [];
};

const ConsentProvider = ({initialGroups = [], children}) => {
    const {isWebView} = useContext(SettingsContext);
    const [groups, setGroups] = useState(initialGroups);

    const updateConsent = useCallback(() => {
        setGroups(activeConsentGroups());
    }, [activeConsentGroups()]);

    // Always allow all if CMP disabled
    const allowsPerformance = !isEnabled(Feature.CMP) || isWebView || groups.includes(ConsentGroup.PERFORMANCE);
    const allowsFunctional = !isEnabled(Feature.CMP) || isWebView || groups.includes(ConsentGroup.FUNCTIONAL);
    const allowsTargeting = !isEnabled(Feature.CMP) || isWebView || groups.includes(ConsentGroup.TARGETING);
    const allowsAll = allowsPerformance && allowsFunctional && allowsTargeting;
    const consent = {groups, allowsPerformance, allowsFunctional, allowsTargeting, allowsAll};

    if (isEnabled(Feature.CMP) && process.browser) {
        useEffect(() => {
            window.addEventListener('consentChange', updateConsent);
            updateConsent();

            return () => {
                window.removeEventListener('consentChange', updateConsent);
            };
        }, []);
    }

    return <ConsentContext.Provider value={consent}>{children}</ConsentContext.Provider>;
};

if (process.env.NODE_ENV === 'development') {
    ConsentProvider.propTypes = {
        initialGroups: PropTypes.array
    };
}

export {ConsentContext};
export default ConsentProvider;
